import { useState, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Utensils, Filter } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useMembership } from '@/hooks/useMembership';
import { usePonds } from '@/hooks/usePonds';
import { RoleGuard } from '@/components/layout/RoleGuard';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { format } from 'date-fns';
import { todayInPH, addPhDays } from '@/lib/phDate';

interface DailyFeedingSummary {
  feeding_date: string;
  pond_id: string;
  pond_name: string;
  total_kg: number | string;
  slot_count: number;
  feed_type: string | null;
}

type DateRange = '7d' | '30d' | '90d' | 'all';

interface FeedingsSearchParams {
  pond?: string;
  range?: DateRange;
}

export const Route = createFileRoute('/_app/feedings')({
  validateSearch: (search: Record<string, unknown>): FeedingsSearchParams => ({
    pond: typeof search.pond === 'string' ? search.pond : undefined,
    range: ['7d', '30d', '90d'].includes(search.range as string)
      ? (search.range as DateRange)
      : undefined,
  }),
  component: () => (
    <RoleGuard allowedRoles={['supervisor', 'owner', 'operator']}>
      <FeedingsPage />
    </RoleGuard>
  ),
});

function toNum(val: number | string | null | undefined): number | null {
  if (val === null || val === undefined) return null;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? null : n;
}

function FeedingsPage() {
  const { membership } = useMembership();
  const { pond: filterPondId, range: filterRange } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [sortField, setSortField] = useState<string>('feeding_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const dateRange = filterRange || 'all';

  // Issue #131: Freeze the reference day to the component's first render so
  // the queryKey doesn't silently change at midnight (which would cause a
  // thundering refetch). Derived via useMemo to stay stable across renders.
  const dateParams = useMemo(() => {
    if (dateRange === 'all') return {} as Record<string, string>;
    const today = todayInPH();
    const days = dateRange === '7d' ? 7 : dateRange === '90d' ? 90 : 30;
    return {
      start_date: addPhDays(today, -days),
      end_date: today,
    };
  }, [dateRange]);

  // Fetch ponds for filter
  const { data: pondsData } = usePonds(membership?.farmId);

  // Fetch daily feeding summaries
  const { data: feedingsData, isLoading } = useQuery({
    queryKey: ['feedings-daily-summary', membership?.farmId, dateParams.start_date, dateParams.end_date],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (dateParams.start_date) params.start_date = dateParams.start_date;
      if (dateParams.end_date) params.end_date = dateParams.end_date;
      const response = await api.get<{ data: DailyFeedingSummary[] }>(
        `/api/v1/farms/${membership?.farmId}/feedings/daily-summary`,
        { params }
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    enabled: !!membership?.farmId,
  });

  // Apply pond filter client-side
  let filtered = feedingsData || [];
  if (filterPondId) {
    filtered = filtered.filter(f => f.pond_id === filterPondId);
  }

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let valA: number | string = 0;
    let valB: number | string = 0;

    if (sortField === 'feeding_date') {
      valA = a.feeding_date;
      valB = b.feeding_date;
    } else if (sortField === 'total_kg') {
      valA = toNum(a.total_kg) ?? 0;
      valB = toNum(b.total_kg) ?? 0;
    } else if (sortField === 'pond_name') {
      valA = a.pond_name || '';
      valB = b.pond_name || '';
    }

    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Summary cards
  const totalKg = filtered.reduce((s, f) => s + (toNum(f.total_kg) ?? 0), 0);
  const uniqueDates = new Set(filtered.map(f => f.feeding_date));
  const feedingDays = uniqueDates.size;
  const dailyAvg = feedingDays > 0 ? totalKg / feedingDays : 0;
  const uniquePonds = new Set(filtered.map(f => f.pond_id));
  const pondsFed = uniquePonds.size;

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feedings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Daily feeding summary across all ponds
          </p>
        </div>
        <Badge variant="neutral" size="md">
          {filtered.length} {filtered.length === 1 ? 'record' : 'records'}
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SummaryCard label="Total Feed" value={`${totalKg.toFixed(0)} kg`} />
        <SummaryCard label="Feeding Days" value={String(feedingDays)} />
        <SummaryCard label="Daily Average" value={dailyAvg > 0 ? `${dailyAvg.toFixed(1)} kg` : '\u2014'} />
        <SummaryCard label="Ponds Fed" value={String(pondsFed)} />
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-gray-400" />

        <select
          value={dateRange}
          onChange={(e) => navigate({ search: (prev) => ({ ...prev, range: e.target.value === 'all' ? undefined : e.target.value as DateRange }) })}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="all">All time</option>
        </select>

        <select
          value={filterPondId || ''}
          onChange={(e) => navigate({ search: (prev) => ({ ...prev, pond: e.target.value || undefined }) })}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Ponds</option>
          {[...(pondsData || [])].sort((a, b) => a.name.localeCompare(b.name)).map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Feedings Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <SortHeader label="Date" field="feeding_date" current={sortField} dir={sortDir} onSort={toggleSort} />
                <SortHeader label="Pond" field="pond_name" current={sortField} dir={sortDir} onSort={toggleSort} />
                <SortHeader label="Total (kg)" field="total_kg" current={sortField} dir={sortDir} onSort={toggleSort} align="right" />
                <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Feedings</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Feed Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400">
                    <Utensils className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No feeding records found
                  </td>
                </tr>
              ) : (
                sorted.map((f) => {
                  const kg = toNum(f.total_kg);

                  return (
                    <tr key={`${f.feeding_date}-${f.pond_id}`} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                        {format(new Date(f.feeding_date), 'MMM d, yyyy')}
                      </td>
                      <td className="py-3 px-4 text-gray-700 whitespace-nowrap">{f.pond_name}</td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {kg?.toFixed(1) ?? '\u2014'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-700">
                        {f.slot_count}
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {f.feed_type || '\u2014'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <Card.Body className="p-4 text-center">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </Card.Body>
    </Card>
  );
}

function SortHeader({
  label,
  field,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  field: string;
  current: string;
  dir: 'asc' | 'desc';
  onSort: (f: string) => void;
  align?: 'left' | 'right';
}) {
  const isActive = current === field;
  return (
    <th
      className={cn(
        'py-3 px-4 font-medium text-xs uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-gray-700 transition-colors',
        align === 'right' ? 'text-right' : 'text-left',
        isActive ? 'text-gray-900' : 'text-gray-500'
      )}
      onClick={() => onSort(field)}
    >
      {label}
      {isActive && (
        <span className="ml-1">{dir === 'asc' ? '\u2191' : '\u2193'}</span>
      )}
    </th>
  );
}
